import { useFormikContext } from 'formik'
import React from 'react'
import * as Api from 'src/api'
import type { TestType } from 'src/api/types'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import type { FormValues } from './types'

export function useProgramFormOptions(args: {
  readonly programTestTypes: readonly TestType[] | undefined
  readonly isProgramLoading: boolean
}) {
  const { programTestTypes, isProgramLoading } = args
  const headers = useAuthenticatedHeaders()
  const { values: formValues } = useFormikContext<FormValues>()
  const facultyId = formValues?.faculty?.trim() ?? ''

  const { data: faculties, isLoading: isLoadingFaculties } = useApi({
    endpoint: Api.getRegimeFaculties,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: facultySchools, isLoading: isLoadingFacultySchools } = useApi({
    endpoint: Api.getAdministratorProgramFacultySchools,
    params: React.useMemo(
      () => ({
        headers,
        args: { id: facultyId },
      }),
      [headers, facultyId]
    ),
    shouldFetch: facultyId !== '',
    suspense: false,
  })

  const { data: degrees, isLoading: isLoadingDegrees } = useApi({
    endpoint: Api.getRegimeDegrees,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: definitions, isLoading: isLoadingDefinitions } = useApi({
    endpoint: Api.getRegimeDefinitions,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const facultyOptions = React.useMemo(
    () => (faculties ?? []).map((f) => ({ label: f.name, value: f.id })),
    [faculties]
  )

  const facultySchoolOptions = React.useMemo(
    () => (facultySchools ?? []).map((fs) => ({ label: fs.name, value: fs.id })),
    [facultySchools]
  )

  const degreeOptions = React.useMemo(() => (degrees ?? []).map((d) => ({ label: d.name, value: d.id })), [degrees])

  const statusOptions = React.useMemo(
    () => (definitions?.statuses ?? []).map((s) => ({ label: s.label, value: s.value })),
    [definitions]
  )

  const testTypeOptions = React.useMemo(
    () => (programTestTypes ?? []).map((tt) => ({ label: tt.name, value: tt.id })),
    [programTestTypes]
  )

  const learningLangOptions = React.useMemo(
    () => [
      { label: 'ქართული', value: 'ka' },
      { label: 'ინგლისური', value: 'en' },
    ],
    []
  )

  const isLoading =
    isLoadingFaculties || isLoadingFacultySchools || isLoadingDegrees || isLoadingDefinitions || isProgramLoading

  return {
    facultyOptions,
    facultySchoolOptions,
    degreeOptions,
    statusOptions,
    testTypeOptions,
    learningLangOptions,
    isLoading,
    isLoadingFacultySchools,
  }
}
